import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Building, Plane } from 'lucide-react';
import { searchLocations } from '../../services/urbanrideService';
import { noAutofillProps } from '../../utils/noAutofill';

const FBOAutocomplete = ({
  airportIcao,
  placeholder = "Select FBO...",
  onFboSelect,
  initialFboName = "",
  className = "",
  disabled = false
}) => {
  const [searchQuery, setSearchQuery] = useState(initialFboName);
  const [isOpen, setIsOpen] = useState(false);
  const [allFbos, setAllFbos] = useState([]);  // Full list from API (never filtered)
  const [filteredFbos, setFilteredFbos] = useState([]);  // Filtered list for display
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [currentAirport, setCurrentAirport] = useState(null);  // Track which airport we loaded FBOs for

  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const selectingRef = useRef(false); // prevent multiple selects from a single click
  const justSelectedRef = useRef(false); // prevent dropdown reopening right after selection

  // Update search query when initialFboName changes
  useEffect(() => {
    setSearchQuery(initialFboName);
  }, [initialFboName]);

  // Load FBOs when airport changes - only if it's a different airport
  useEffect(() => {
    if (airportIcao && airportIcao.length >= 3) {
      // Only reload if airport actually changed
      if (currentAirport !== airportIcao) {
        loadFbosForAirport(airportIcao);
      }
    } else {
      setAllFbos([]);
      setFilteredFbos([]);
      setSearchQuery('');
      setCurrentAirport(null);
    }
  }, [airportIcao, currentAirport]);

  // Filter FBOs locally when search query changes
  useEffect(() => {
    if (!searchQuery || searchQuery.trim().length < 2) {
      // Show all FBOs if no search query
      setFilteredFbos(allFbos);
    } else {
      // Filter locally - no API call
      const searchLower = searchQuery.trim().toLowerCase();
      const filtered = allFbos.filter(fbo =>
        fbo.name.toLowerCase().includes(searchLower) ||
        (fbo.address && fbo.address.toLowerCase().includes(searchLower))
      );
      setFilteredFbos(filtered);
    }
  }, [searchQuery, allFbos]);

  const loadFbosForAirport = useCallback(async (airportCode) => {
    console.log('[FBOAutocomplete] loadFbosForAirport called with:', airportCode);
    if (!airportCode || airportCode.length < 3) {
      console.log('[FBOAutocomplete] Invalid airportCode, returning empty');
      setAllFbos([]);
      setFilteredFbos([]);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);

      // Extract IATA code from ICAO (e.g., KTEB -> TEB) for Urbanride API
      // Urbanride expects IATA codes (3 chars) for the airport parameter
      let iataCode = airportCode;
      if (airportCode.length === 4 && airportCode.startsWith('K')) {
        iataCode = airportCode.substring(1); // Remove 'K' prefix for US airports
      }

      // Use Urbanride API with airport filter - always empty input
      // API format: { input: "", type: "fbo", airport: "TEB" }
      const query = {
        input: '',  // Always empty - get full list, filter locally
        type: 'fbo',
        airport: iataCode
      };

      console.log('[FBOAutocomplete] Calling Urbanride searchLocations with:', query);
      const result = await searchLocations(query);
      console.log('[FBOAutocomplete] Urbanride response:', result);

      // Map response to FBO format - preserve the original airport code
      const fboList = (result.locations || []).map(fbo => ({
        id: fbo.id,
        name: fbo.name,
        address: fbo.details,
        airport_icao: airportCode  // Keep original code for display
      }));
      console.log('[FBOAutocomplete] Loaded', fboList.length, 'FBOs for airport', airportCode);

      setAllFbos(fboList);
      setFilteredFbos(fboList);
      setCurrentAirport(airportCode);  // Mark that we've loaded FBOs for this airport
    } catch (error) {
      console.error('Error loading FBOs:', error);
      setAllFbos([]);
      setFilteredFbos([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleInputChange = useCallback((e) => {
    const newValue = e.target.value;
    setSearchQuery(newValue);
    setSelectedIndex(-1);
    // Filtering is done automatically via useEffect - no API call needed
  }, []);

  const handleFboSelect = useCallback((fbo) => {
    if (selectingRef.current) return; // guard against duplicate firing
    selectingRef.current = true;
    // Format display as "FBO Name / ICAO"
    const displayValue = `${fbo.name} / ${fbo.airport_icao}`;
    setSearchQuery(displayValue);
    setIsOpen(false);
    setSelectedIndex(-1);
    justSelectedRef.current = true;
    // Defocus input to prevent focus-driven reopen
    if (inputRef.current) {
      requestAnimationFrame(() => inputRef.current && inputRef.current.blur());
    }

    // Notify parent with complete FBO data
    if (onFboSelect) {
      onFboSelect({
        ...fbo,
        displayName: displayValue
      });
    }
    // release the guard after the macrotask so blur/click won't re-fire
    setTimeout(() => { selectingRef.current = false; }, 0);
  }, [onFboSelect]);

  const handleKeyDown = useCallback((e) => {
    if (!isOpen || filteredFbos.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => {
          const newIndex = prev < filteredFbos.length - 1 ? prev + 1 : 0;
          // Scroll to the selected item
          setTimeout(() => {
            const dropdownElement = dropdownRef.current;
            const selectedElement = dropdownElement?.querySelector(`[data-index="${newIndex}"]`);
            if (selectedElement && dropdownElement) {
              selectedElement.scrollIntoView({ 
                block: 'nearest', 
                behavior: 'smooth' 
              });
            }
          }, 0);
          return newIndex;
        });
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => {
          const newIndex = prev > 0 ? prev - 1 : filteredFbos.length - 1;
          // Scroll to the selected item
          setTimeout(() => {
            const dropdownElement = dropdownRef.current;
            const selectedElement = dropdownElement?.querySelector(`[data-index="${newIndex}"]`);
            if (selectedElement && dropdownElement) {
              selectedElement.scrollIntoView({ 
                block: 'nearest', 
                behavior: 'smooth' 
              });
            }
          }, 0);
          return newIndex;
        });
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < filteredFbos.length) {
          handleFboSelect(filteredFbos[selectedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSelectedIndex(-1);
        break;
      default:
        break;
    }
  }, [isOpen, filteredFbos, selectedIndex, handleFboSelect]);

  const handleFocus = useCallback(() => {
    if (justSelectedRef.current) {
      // avoid immediate reopen after a selection
      justSelectedRef.current = false;
      return;
    }
    if (airportIcao) {
      setIsOpen(true);
      if (allFbos.length === 0) {
        loadFbosForAirport(airportIcao);
      }
    }
  }, [airportIcao, allFbos.length, loadFbosForAirport]);

  const handleBlur = useCallback((e) => {
    // Only hide dropdown if focus moved completely outside the component
    const relatedTarget = e.relatedTarget;
    
    // Don't close if focus moved to the dropdown
    if (dropdownRef.current?.contains(relatedTarget)) {
      return;
    }
    
    // Don't close if focus moved to another element within the same modal
    if (relatedTarget && relatedTarget.closest('[role="dialog"]')) {
      return;
    }
    
    // Delay hiding dropdown to allow clicks on dropdown items
    setTimeout(() => {
      if (!dropdownRef.current?.contains(document.activeElement)) {
        setIsOpen(false);
        setSelectedIndex(-1);
      }
    }, 200);
  }, []);

  // Show dropdown only when user is interacting (avoid auto-opening on edit)
  useEffect(() => {
    const inputFocused = document.activeElement === inputRef.current;
    if ((filteredFbos.length > 0 || isLoading) && inputFocused) {
      setIsOpen(true);
    } else if (!inputFocused && !searchQuery) {
      // Keep closed when not focused and we just loaded edit state
      setIsOpen(false);
    }
  }, [filteredFbos, isLoading, searchQuery]);

  return (
    <div className="relative">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          {...noAutofillProps}
          name="fbo-search"
          value={searchQuery}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder={airportIcao ? placeholder : "Select airport first..."}
          disabled={disabled || !airportIcao}
          className={`w-full pl-10 pr-4 py-3 border border-ui-line rounded-lg focus:outline-none focus:ring-2 focus:ring-ui-violet-solid focus:border-transparent text-ui-ink placeholder-ui-faint ${
            !airportIcao ? 'bg-ui-hover text-ui-muted' : ''
          } ${className}`}
        />
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Building className="h-5 w-5 text-ui-muted" />
        </div>
        {isLoading && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-ui-violet-solid"></div>
          </div>
        )}
      </div>

      {/* Dropdown */}
      {isOpen && airportIcao && (
        <div 
          ref={dropdownRef}
          className="absolute z-[9999] w-full mt-1 bg-ui-surface border border-ui-line rounded-lg shadow-xl max-h-64 overflow-y-auto"
          style={{ zIndex: 9999 }}
        >
          {isLoading && (
            <div className="px-4 py-3 text-ui-muted text-center">
              <div className="flex items-center justify-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-ui-violet-solid"></div>
                Loading FBOs for {airportIcao}...
              </div>
            </div>
          )}

          {!isLoading && filteredFbos.length === 0 && (
            <div className="px-4 py-3 text-ui-muted text-center">
              {searchQuery.length >= 2 ? 
                `No FBOs found matching "${searchQuery}" at ${airportIcao}` :
                `No FBOs found at ${airportIcao}`
              }
            </div>
          )}

          {!isLoading && filteredFbos.length > 0 && (
            <>
              <div className="px-3 py-2 bg-ui-sunk border-b border-ui-line">
                <div className="text-xs font-medium text-ui-ink-3">
                  {filteredFbos.length} FBO{filteredFbos.length !== 1 ? 's' : ''} at {airportIcao}
                </div>
              </div>
              {filteredFbos.map((fbo, index) => (
                <button
                  type="button"
                  key={fbo.id || `${fbo.name}-${index}`}
                  data-index={index}
                  onMouseDown={(e) => {
                    // Use onMouseDown for immediate selection and to avoid input blur interfering
                    e.preventDefault();
                    e.stopPropagation();
                    handleFboSelect(fbo);
                  }}
                  onMouseEnter={() => setSelectedIndex(index)}
                  className={`w-full text-left px-4 py-3 cursor-pointer border-b border-ui-divider last:border-b-0 hover:bg-ui-violet-bg transition-colors duration-150 ${
                    index === selectedIndex ? 'bg-ui-violet-bg' : ''
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-1">
                      <div className="w-8 h-8 bg-gradient-to-br from-ui-blue-strong to-ui-indigo-strong rounded-full flex items-center justify-center">
                        <Building className="w-4 h-4 text-ui-on-primary" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-ui-ink truncate mb-1">
                        {fbo.name} / {fbo.airport_icao}
                      </div>
                      {fbo.address && (
                        <div className="text-sm text-ui-ink-3 mb-1">
                          {fbo.address}
                        </div>
                      )}
                      <div className="flex items-center gap-4 text-xs text-ui-muted">
                        {fbo.phone && (
                          <span>📞 {fbo.phone}</span>
                        )}
                        {fbo.email && (
                          <span>📧 {fbo.email}</span>
                        )}
                      </div>
                      {fbo.services && fbo.services.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {fbo.services.slice(0, 3).map((service) => (
                            <span key={service} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-ui-blue-bg text-ui-blue-fg">
                              {service.replace('_', ' ')}
                            </span>
                          ))}
                          {fbo.services.length > 3 && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-ui-hover text-ui-ink-3">
                              +{fbo.services.length - 3} more
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </>
          )}

          {!airportIcao && (
            <div className="px-4 py-3 text-ui-muted text-center">
              Please select an airport first to see available FBOs
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FBOAutocomplete;